// Belebte Höfe (Konzept §11): „Lichter an, Leute arbeiten, irgendwo rangiert
// was. Du gehst denen aus dem Weg, weil es vernünftig ist, nicht weil ein
// Trigger feuert. Kein Fangen, kein Rauswurf, nur Anwesenheit."
//
// Reine Weltschicht: deterministische Positionen aus der Weltzeit. Alle Spieler
// sehen dieselben Leute an derselben Stelle (Konzept §5). Kein Sichtkegel,
// keine Verfolgung, keine Reaktion auf den Spieler.
//
// Anzeichen (Konzept §16 Frage 3 — Warten als Beobachten):
// Kurz vor einer LOCAL-Abfahrt und bei Zugannäherung wechseln die Patrouillen
// auf feste „Arbeitspositionen" am Gleis. Wer am Hof liegt, sieht die Bewegung
// und kann schließen: gleich passiert etwas — ohne UI, ohne Beschriftung (§4/§8).
//
// StationSystem (Client) fragt workersAt(worldSeconds) ab und zeichnet Figuren.

import {
  STATIONS, BRANCH_MAIN, trackPoint, trackRight, trackTangent, railTopY,
} from './track.js';
import { isNight } from './worldtime.js';
import { hashInt } from './rng.js';
import { yardActivityAt, allTrainsAt, trainDistanceTo } from './schedule.js';

const SOUTH = STATIONS[0];

/**
 * Patrouillen-Wegpunkte relativ zur Hauptstrecke (s, lateral).
 * Nur Südhof: activity 0,55 — belebter Hof. Nord ist ruhig (activity 0,2)
 * und bekommt bewusst keine Arbeiter (Kontrast belebt vs. tot).
 */
const SOUTH_PATROLS = [
  {
    id: 'w-yard-west',
    points: [
      { s: 120, lateral: -7.5 },
      { s: 220, lateral: -8.0 },
      { s: 340, lateral: -10.5 },
      { s: 480, lateral: -9.0 },
      { s: 560, lateral: -7.0 },
      { s: 400, lateral: -11.0 },
      { s: 250, lateral: -8.5 },
    ],
    periodWorld: 0,
    phase: 0.0,
    role: 'walker',
    // Δlateral ≤ ~1,2 m gegenüber typischer Patrouille — sichtbar, stetig.
    alertLateral: -6.8,
  },
  {
    id: 'w-platform',
    points: [
      { s: 180, lateral: 5.5 },
      { s: 280, lateral: 6.0 },
      { s: 380, lateral: 5.8 },
      { s: 480, lateral: 6.2 },
      { s: 320, lateral: 7.5 },
      { s: 200, lateral: 6.5 },
    ],
    periodWorld: 0,
    phase: 0.31,
    role: 'walker',
    alertLateral: 5.0,
  },
  {
    id: 'w-bunker',
    points: [
      { s: 450, lateral: -11.5 },
      { s: 500, lateral: -12.0 },
      { s: 530, lateral: -11.0 },
      { s: 500, lateral: -12.5 },
    ],
    periodWorld: 0,
    phase: 0.67,
    role: 'worker',
    alertLateral: -10.5,
  },
  {
    id: 'w-east-siding',
    points: [
      { s: 150, lateral: 9.5 },
      { s: 260, lateral: 10.0 },
      { s: 400, lateral: 9.0 },
      { s: 520, lateral: 10.5 },
      { s: 350, lateral: 11.0 },
    ],
    periodWorld: 0,
    phase: 0.12,
    role: 'walker',
    alertLateral: 8.8,
  },
  {
    id: 'w-crossing',
    points: [
      { s: 300, lateral: -6.0 },
      { s: 310, lateral: 0.0 },
      { s: 300, lateral: 6.0 },
      { s: 290, lateral: 0.0 },
    ],
    periodWorld: 0,
    phase: 0.48,
    role: 'walker',
    // Kein großer Versatz: Kreuzungspfad hat lateral 0…±6, Alert nur leicht weg.
    alertLateral: null,
    alertLateralDelta: -1.2, // relativ zur aktuellen lateral
  },
];

// Gehgeschwindigkeit Echtzeit m/s → Welt m/s.
const WALK_REAL_MPS = 0.9;
const TIME_SCALE_WALK = 12;
const WALK_WORLD_MPS = WALK_REAL_MPS / TIME_SCALE_WALK;

function pathLength(points) {
  let len = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    len += Math.hypot(b.s - a.s, b.lateral - a.lateral);
  }
  return len;
}

function worldPosOnPath(points, distAlong) {
  let total = pathLength(points);
  if (total < 1e-6) {
    const p = points[0];
    return { s: p.s, lateral: p.lateral, heading: 0, ds: 1, dLat: 0 };
  }
  let d = ((distAlong % total) + total) % total;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const seg = Math.hypot(b.s - a.s, b.lateral - a.lateral);
    if (d <= seg || i === points.length - 1) {
      const u = seg > 1e-6 ? d / seg : 0;
      const s = a.s + (b.s - a.s) * u;
      const lateral = a.lateral + (b.lateral - a.lateral) * u;
      return {
        s,
        lateral,
        heading: Math.atan2(b.lateral - a.lateral, b.s - a.s),
        ds: b.s - a.s,
        dLat: b.lateral - a.lateral,
      };
    }
    d -= seg;
  }
  const p = points[0];
  return { s: p.s, lateral: p.lateral, heading: 0, ds: 1, dLat: 0 };
}

for (const patrol of SOUTH_PATROLS) {
  const len = pathLength(patrol.points);
  const speed = patrol.role === 'worker' ? WALK_WORLD_MPS * 0.45 : WALK_WORLD_MPS;
  patrol.periodWorld = Math.max(120, len / speed);
  patrol._pathLen = len;
}

/**
 * Nacht: weniger Leute. Deterministisch aus ID, welche Schicht bleibt.
 */
function isOnShift(workerId, worldSeconds) {
  if (!isNight(worldSeconds)) return true;
  return (hashInt(0x70ff, workerId.charCodeAt(2) || 0, workerId.length) & 1) === 0;
}

/**
 * Stetiger Blend 0..1.
 * Pro Zug eine stetige proximity — max davon ist stetig (im Gegensatz zu
 * nearestDist, das beim Wechsel des nächsten Zuges springt).
 */
function alertBlend(activity, workerX, workerY, workerZ, worldSeconds) {
  let b = 0;
  if (activity?.preDepart && activity.departInWorld != null) {
    const pre = 1 - Math.min(1, Math.max(0, activity.departInWorld / (8 * 60)));
    b = Math.max(b, pre);
  }
  // Fallback ohne Position: activity.alert (stetig genug für Tests grob).
  if (workerX == null) {
    if (activity && Number.isFinite(activity.nearestDist) && activity.nearestDist < 900) {
      b = Math.max(b, ((1 - activity.nearestDist / 900) ** 1.15) * 0.9);
    }
    return Math.min(1, b);
  }
  const trains = allTrainsAt(worldSeconds);
  for (const tr of trains) {
    const d = trainDistanceTo(tr, workerX, workerY, workerZ);
    if (d < 900) {
      const prox = (1 - d / 900) ** 1.15;
      b = Math.max(b, prox * 0.9);
    }
  }
  return Math.min(1, b);
}

/**
 * Arbeiterpositionen zur Weltzeit.
 * @param {number} worldSeconds
 * @returns {Array<object>}
 */
export function workersAt(worldSeconds) {
  const t = Math.max(0, worldSeconds);
  const activity = yardActivityAt(t, 'south');
  const out = [];

  for (const patrol of SOUTH_PATROLS) {
    if (!isOnShift(patrol.id, t)) continue;

    const dist = (t / patrol.periodWorld + patrol.phase) * patrol._pathLen;
    const wp = worldPosOnPath(patrol.points, dist);

    // Erst ungestörte Weltposition, dann Blend aus Zugdistanzen (stetig).
    const p0 = trackPoint(wp.s, { x: 0, y: 0, z: 0 }, BRANCH_MAIN);
    const r0 = trackRight(wp.s, { x: 0, y: 0, z: 0 }, BRANCH_MAIN);
    const x0 = p0.x + r0.x * wp.lateral;
    const z0 = p0.z + r0.z * wp.lateral;
    const y0 = railTopY(wp.s, wp.lateral, BRANCH_MAIN);
    const blend = alertBlend(activity, x0, y0, z0, t);

    // Nur lateraler Versatz. |Δlateral| ≤ ~2 m → Schritt bei stetigem Blend klein.
    let s = wp.s;
    let lateral = wp.lateral;
    let ds = wp.ds;
    let dLat = wp.dLat;
    if (blend > 0) {
      if (patrol.alertLateral != null) {
        // Ziel-Lateral absolut, aber auf max. 1,2 m Versatz gekappt.
        const raw = patrol.alertLateral - wp.lateral;
        const cap = Math.max(-1.2, Math.min(1.2, raw));
        lateral = wp.lateral + cap * blend;
        dLat = cap;
      } else if (patrol.alertLateralDelta != null) {
        const cap = Math.max(-1.2, Math.min(1.2, patrol.alertLateralDelta));
        lateral = wp.lateral + cap * blend;
        dLat = cap;
      }
    }

    const p = trackPoint(s, { x: 0, y: 0, z: 0 }, BRANCH_MAIN);
    const r = trackRight(s, { x: 0, y: 0, z: 0 }, BRANCH_MAIN);
    const f = trackTangent(s, { x: 0, y: 0, z: 0 }, BRANCH_MAIN);
    const moveX = f.x * ds + r.x * dLat;
    const moveZ = f.z * ds + r.z * dLat;
    const heading = Math.atan2(moveX, moveZ);

    const x = p.x + r.x * lateral;
    const z = p.z + r.z * lateral;
    const y = railTopY(s, lateral, BRANCH_MAIN);

    const moving = blend > 0.55
      ? false
      : (patrol.role !== 'worker' || (Math.sin(t * 0.02 + patrol.phase * 6) > -0.3));

    out.push({
      id: patrol.id,
      station: 'south',
      x, y, z,
      heading,
      role: patrol.role,
      moving,
      s,
      lateral,
      alert: blend > 0.2,
      yardPhase: activity.phase,
    });
  }

  return out;
}

/**
 * Grob: Hof-Bounding in Welt-XZ für Tests (Südhof).
 */
export function southYardBounds() {
  const samples = [];
  for (let s = SOUTH.yardStart; s <= SOUTH.yardEnd; s += 20) {
    for (const lat of [-14, 0, 14]) {
      const p = trackPoint(s, { x: 0, y: 0, z: 0 }, BRANCH_MAIN);
      const r = trackRight(s, { x: 0, y: 0, z: 0 }, BRANCH_MAIN);
      samples.push({ x: p.x + r.x * lat, z: p.z + r.z * lat });
    }
  }
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const p of samples) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  const pad = 8;
  return { minX: minX - pad, maxX: maxX + pad, minZ: minZ - pad, maxZ: maxZ + pad };
}

export const WORKER_INFO = Object.freeze({
  southCount: SOUTH_PATROLS.length,
  walkRealMps: WALK_REAL_MPS,
  stationsWithWorkers: ['south'],
  // Arbeiter reagieren auf Fahrplan-Phasen (sichtbares Anzeichen).
  reactsToYardActivity: true,
});
